import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import T from 'prop-types';

import { Card } from 'components/oldCard/Card.styled';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MACHINE_STATUS_SIMPLE_MAP } from 'attrs/notifications';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadGraphsTemplates,
  loadSensorAverage,
  loadSensorHistory,
  loadStatusHistory
} from 'redux/graphTemplates/actions';
import { getAllGraphTemplates } from 'redux/graphTemplates/selectors';
import {
  ACCESS_TYPE_ALL_ORG,
  getDateRange,
  getFormattedDate,
  getLineStyle,
  PERIOD_TYPE_LAST_8_HOURS
} from 'attrs/chartsTemplate';
import MobileSensorPortrait from 'components/elements/MobileSensorPortrait';
import ReactEcharts from 'echarts-for-react';
import ChartTemplateFooterContainer from 'components/ChartTemplateFooter';
import { getGraphsTemplatesReducerState } from 'redux/rootSelectors';
import { calculateValue, determineDirection, findClosestStatusDate, sideSpace, statusMap } from 'helpers/chartUtils';
import { debounce, getMachineStatusProps, isNumber, isSuccess } from 'helpers/utils';
import { getMillSensor } from 'attrs/sensorType';
import { getValueToNullableCell } from 'helpers/stringHandler';
import { useHistory } from 'react-router-dom';
import { INVALID_DATE_FORMAT } from 'helpers/consts';

// Total height of the chart grid in pixels
const chartPixelHeight = 410;
// Total height of the chart container in pixels
const chartPixelHeightContainer = 580;

// a specific rule for batch in progress
const normalizeEndDate = endDate => {
  if (endDate === INVALID_DATE_FORMAT) return new Date().toISOString();
  return endDate;
};

const SensorsChart = ({ machineId, date, chartRef, footerRef }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [templateId, setTemplateId] = useState('');
  const [template, setTemplate] = useState({});
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const templateList = useSelector(getAllGraphTemplates);

  const xAxisDefaultValue = [
    {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        formatter: '{dd}/{MM}/{yyyy}\n{hh}:{mm}:{ss}'
      },
      axisPointer: {
        show: true,
        type: 'line',
        lineStyle: {
          color: 'gray',
          width: 1,
          type: 'dashed',
          shadowBlur: 3,
          shadowColor: 'rgba(0, 0, 0, 1)'
        }
      }
    }
  ];

  const yAxisDefaultValue = [
    {
      data: []
    }
  ];

  const seriesDefaultValue = [
    {
      data: [],
      type: 'line'
    }
  ];

  const [resumedSensorMap, setResumedSensorMap] = useState(new Map());
  const [sensorVisibilityList, setSensorVisibilityList] = useState([]);
  const [loadSensorParamsList, setLoadSensorParamsList] = useState([]);
  const [loadStatusParam, setLoadStatusParam] = useState();

  const loadSensorHistoryStatusEqual = (oldObj, newObj) =>
    oldObj?.loadSensorHistoryStatus?.status === newObj?.loadSensorHistoryStatus?.status;
  const { loadSensorHistoryStatus } = useSelector(getGraphsTemplatesReducerState, loadSensorHistoryStatusEqual);

  const loadSensorAverageStatusEqual = (oldObj, newObj) =>
    oldObj?.loadSensorAverageStatus?.status === newObj?.loadSensorAverageStatus?.status;
  const { loadSensorAverageStatus } = useSelector(getGraphsTemplatesReducerState, loadSensorAverageStatusEqual);

  const sensorHistoryResponseEqual = (oldObj, newObj) => {
    const sameSize = oldObj?.sensorHistoryResponse?.size === newObj?.sensorHistoryResponse?.size;
    const biggerThenZero = oldObj?.sensorHistoryResponse?.size > 0;
    const equalsObj = Array.from(oldObj?.sensorHistoryResponse?.keys()).every(
      key =>
        JSON.stringify(oldObj?.sensorHistoryResponse?.get(key) || '') ===
        JSON.stringify(newObj?.sensorHistoryResponse?.get(key) || '')
    );
    return sameSize && equalsObj && !biggerThenZero;
  };
  const { sensorHistoryResponse } = useSelector(getGraphsTemplatesReducerState, sensorHistoryResponseEqual);

  const sensorAverageResponseEqual = (oldObj, newObj) => {
    const sameSize = oldObj?.sensorAverageResponse?.size === newObj?.sensorAverageResponse?.size;
    const biggerThenZero = oldObj?.sensorAverageResponse?.size > 0;
    const equalsObj = Array.from(oldObj?.sensorAverageResponse?.keys()).every(
      key =>
        JSON.stringify(oldObj?.sensorAverageResponse?.get(key) || '') ===
        JSON.stringify(newObj?.sensorAverageResponse?.get(key) || '')
    );
    return sameSize && equalsObj && !biggerThenZero;
  };
  const { sensorAverageResponse } = useSelector(getGraphsTemplatesReducerState, sensorAverageResponseEqual);

  const { loadStatusHistoryStatus, statusHistoryResponse } = useSelector(getGraphsTemplatesReducerState);
  const [sensorHistoryMap, setSensorHistoryMap] = useState(new Map());
  const [sensorAverageMap, setSensorAverageMap] = useState(new Map());
  const [statusHistoryList, setStatusHistoryList] = useState([]);
  const [yAxis, setYAxis] = useState([]);
  const [series, setSeries] = useState([]);
  const [updateStatus, setUpdateStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingTimeOut, setLoadingTimeOut] = useState(0);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [checkedShowMachineStatus, setCheckedShowMachineStatus] = useState(true);
  const [dataZoomRange, setDataZoomRange] = useState({ start: 0, end: 100 });

  /**
   * A specific function to find sensor attributes; can be used only in this component
   * @param {*} resultsParam
   * @returns
   */
  function findSensorAttributes(resultsParam) {
    return resultsParam.map(result => {
      const sensor = template.sensors.find(s => s.id === result.sensorId);
      const millSensor = getMillSensor(sensor.type);
      const name = t(millSensor?.name || sensor?.name);
      const unit = millSensor?.unit || sensor?.custom_unit || sensor?.unit || 'n/a';
      return {
        id: sensor.id,
        name,
        unit: sensor.is_static ? '' : unit,
        color: sensor.line_color,
        value: result.closestEntry?.v,
        isStatic: sensor.is_static
      };
    });
  }

  /**
   * Renders a colored circle only when the sensor is static.
   * If the sensor is not static (or is missing), it returns an empty string.
   *
   * @param {*} sensor The sensor object containing `isStatic` and `color`.
   * @returns {string} HTML string for the circle component or an empty string.
   */
  const renderCircle = sensor => {
    if (sensor?.isStatic) return '';
    return `
      <span
        style="
          display: inline-block;
          margin-right: 4px;
          border-radius: 10px;
          width: 10px;
          height: 10px;
          background-color: ${sensor?.color};
        "
      ></span>
    `;
  };

  useEffect(() => {
    // check if template is already loaded
    if (templateId) {
      setSeries([]);
      // needed to work with the status line
      setYAxis([
        {
          isStatusYAxis: true,
          type: 'value',
          min: 0,
          max: 100,
          show: false // hide this axis visually
        }
      ]);
      setLoading(true);
      const hasBatchRange = Boolean(date?.start && date?.end);
      const templateRange = template?.period_type || PERIOD_TYPE_LAST_8_HOURS;
      const [pastDate, currentDate] = hasBatchRange
        ? [date?.start, normalizeEndDate(date?.end)]
        : getDateRange(templateRange);
      const tmpResumedSensorMap = new Map();
      const tmpSensorVisibilityList = [];
      const tempSensorParamsList = [];
      const tempStatusParam = {
        machineId: template.machine_id,
        from: pastDate,
        to: currentDate
      };

      template.sensors.forEach((s, i) => {
        const millSensor = getMillSensor(s?.type);
        const name = millSensor?.name || s?.name;
        const unit = millSensor?.unit || s?.custom_unit || s?.unit || 'n/a';

        const resumedSensor = {
          sensorId: s?.id,
          name,
          isCustom: s?.is_custom,
          isStatic: s?.is_static,
          type: s?.type,
          customIconType: s?.custom_icon_type,
          staticIconType: s?.static_icon_type,
          unit,
          productionLine: s?.production_line,
          machineName: s?.machine_name,
          lineColor: s?.line_color,
          lineStyle: s?.line_style,
          lineThickness: s?.line_thickness
        };
        tmpResumedSensorMap.set(s?.id, resumedSensor);
        tmpSensorVisibilityList.push(true);
        tempSensorParamsList.push({
          sensorId: s?.id,
          machineId: s?.machine_id,
          from: pastDate,
          to: currentDate,
          unit,
          sensor_type: s?.type
        });

        const transLatedName = t(`${name}`);
        const yAxisSensor = {
          sensorId: s?.id,
          min: isNumber(s?.min_scale) ? s?.min_scale : 'dataMin',
          max: isNumber(s?.max_scale) ? s?.max_scale : 'dataMax',
          type: 'value',
          name: unit,
          show: false,
          axisLine: {
            show: true,
            lineStyle: {
              color: s?.line_color
            }
          },
          nameRotate: 45,
          nameTextStyle: {
            fontSize: 14,
            fontWeight: 'bold'
          },
          axisLabel: {
            show: true
          },
          splitLine: {
            show: false
          }
        };
        const seriesSensor = {
          sensorId: s?.id,
          name: transLatedName,
          type: 'line',
          symbol: 'none',
          color: s?.line_color,
          lineStyle: {
            width: s?.line_thickness || 1,
            type: getLineStyle(s?.line_style),
            simplify: true,
            opacity: 1
          },
          yAxisIndex: i + 1,
          data: [],
          show: true,
          itemStyle: { opacity: 1 },
          progressive: 5000,
          progressiveThreshold: 10000,
          large: true,
          largeThreshold: 2000,
          z: 2,
          zlevel: 0
        };
        setYAxis(prevYAxis => (!prevYAxis?.length ? [yAxisSensor] : [...prevYAxis, yAxisSensor]));
        setSeries(prevSeries => (!prevSeries?.length ? [seriesSensor] : [...prevSeries, seriesSensor]));
      });
      setResumedSensorMap(tmpResumedSensorMap);
      setSensorVisibilityList(tmpSensorVisibilityList);
      setLoadSensorParamsList(tempSensorParamsList);
      setLoadStatusParam(tempStatusParam);
    }
  }, [template]);

  useEffect(() => {
    dispatch(loadSensorHistory(loadSensorParamsList));
    dispatch(loadSensorAverage(loadSensorParamsList));
  }, [dispatch, loadSensorHistory, loadSensorAverage, loadSensorParamsList]);

  useEffect(() => {
    if (isSuccess(loadSensorHistoryStatus.status)) {
      setSensorHistoryMap(sensorHistoryResponse);
    }
  }, [loadSensorHistoryStatus, sensorHistoryResponse]);

  useEffect(() => {
    if (isSuccess(loadSensorAverageStatus.status)) {
      setSensorAverageMap(sensorAverageResponse);
    }
  }, [loadSensorAverageStatus, sensorAverageResponse]);

  useEffect(() => {
    if (templateId) {
      setTemplate(templateList.find(tem => tem.id === templateId));
    }
  }, [templateId]);

  useEffect(() => {
    if (sensorHistoryMap.size && templateId) {
      const isStaticMap = new Map();
      sensorHistoryMap.forEach((value, key) => {
        const foundSensor = template?.sensors?.find(s => s.id === key);
        const isStatic = foundSensor?.is_static || false;
        isStaticMap.set(key, isStatic);
        const type = foundSensor?.type;
        const customUnit = foundSensor?.custom_unit;
        const unit = foundSensor?.unit;
        const millSensor = getMillSensor(type);
        const unitDef = millSensor?.unit || customUnit || unit || 'n/a';

        const newValue =
          value?.map(m => ({
            name: new Date(m.t).toISOString(),
            value: [new Date(m.t).toISOString(), m.v],
            unit: isStatic ? '' : unitDef,
            machineId,
            sensorId: key,
            isStatic,
            time: m.t
          })) || [];

        setSeries(prevSeries => prevSeries.map(s => (s?.sensorId === key ? { ...s, data: newValue } : s)));
      });

      let yAxisIndex = 0;
      setYAxis(prevYAxis =>
        prevYAxis.map(y => {
          if (isStaticMap.get(y.sensorId)) return y;
          const willShow = sensorHistoryMap.has(y.sensorId);
          const ny = {
            ...y,
            show: willShow,
            position: determineDirection(yAxisIndex),
            offset: calculateValue(yAxisIndex)
          };
          if (willShow) yAxisIndex += 1;
          return ny;
        })
      );
    }
  }, [sensorHistoryMap, setSeries]);

  useEffect(() => {
    dispatch(loadStatusHistory(loadStatusParam));
  }, [dispatch, loadStatusHistory, loadStatusParam]);

  useEffect(() => {
    if (isSuccess(loadStatusHistoryStatus.status) && !updateStatus) {
      setStatusHistoryList(statusHistoryResponse);
      setUpdateStatus(true);
    }
  }, [loadStatusHistoryStatus, statusHistoryResponse]);

  useEffect(() => {
    // 'hasStatusSeries' is only used because of hot reload in strict mode
    const hasStatusSeries = series.some(s => s.name.includes('STATUS-'));

    if (sensorHistoryMap?.size && statusHistoryList?.length && (updateStatus || !hasStatusSeries)) {
      setUpdateStatus(false);

      // Build periods from the statusHistoryList
      const statusPeriods = [];
      statusHistoryList.forEach((status, index) => {
        const nextItem = index < statusHistoryList.length - 1 ? statusHistoryList[index + 1] : status;
        const strCurrentDate = new Date(status.t).toISOString();
        const strNextDate = new Date(nextItem.t).toISOString();
        statusPeriods.push({
          start: strCurrentDate,
          end: strNextDate,
          status: status.v,
          color: statusMap[status.v],
          warning: status.w
        });
      });

      // Build series objects from periods
      const built = Object.values(
        statusPeriods.reduce((acc, period) => {
          const statusKey = `STATUS-${period.status}`;

          if (!acc[statusKey]) {
            acc[statusKey] = {
              name: statusKey,
              type: 'line',
              smooth: false,
              show: checkedShowMachineStatus,
              symbol: 'none',
              yAxisIndex: 0,
              z: 1,
              zlevel: 0,
              lineStyle: {
                width: 12,
                color: period.color,
                simplify: true,
                opacity: checkedShowMachineStatus ? 0.6 : 0
              },
              data: []
            };
          }

          // Start the period with the beginning point
          acc[statusKey].data.push({
            name: new Date(period.start).toISOString(),
            value: [new Date(period.start).toISOString(), 1.6],
            time: period.start
          });

          // End the period with the ending point and add a null to create a gap
          acc[statusKey].data.push(
            {
              name: new Date(period.end).toISOString(),
              value: [new Date(period.end).toISOString(), 1.6],
              time: period.end
            },
            null // gap between this and the next period
          );

          if (period.warning) {
            const warningKey = 'STATUS-WARNING';
            if (!acc[warningKey]) {
              acc[warningKey] = {
                name: warningKey,
                type: 'line',
                smooth: false,
                show: true,
                symbol: 'none',
                yAxisIndex: 0,
                z: 1,
                zlevel: 0,
                lineStyle: {
                  width: 6,
                  color: '#F8CC84',
                  simplify: true,
                  opacity: 0.6
                },
                data: []
              };
            }

            acc[warningKey].data.push({
              name: new Date(period.start).toISOString(),
              value: [new Date(period.start).toISOString(), 3.9],
              time: period.start
            });

            acc[warningKey].data.push(
              {
                name: new Date(period.end).toISOString(),
                value: [new Date(period.end).toISOString(), 3.9],
                time: period.end
              },
              null
            );
          }

          return acc;
        }, {})
      );

      setSeries(prev => (!prev?.length ? built : [...prev.filter(s => !s.name.includes('STATUS-')), ...built]));

      // Loading delay logic (unchanged, but fixed Map handling)
      const totalItems =
        sensorHistoryMap instanceof Map
          ? Array.from(sensorHistoryMap.values()).reduce((max, arr) => Math.max(max, arr?.length || 0), 0)
          : 0;

      // Set a delay based on the totalItems
      let delay = totalItems * 0.3;
      // Set a minimum delay of 3-second time needed to load the chart
      delay = delay < 3000 ? 3000 : delay;

      clearTimeout(loadingTimeOut);
      setLoadingTimeOut(
        setTimeout(() => {
          setLoading(false);
        }, delay)
      );
    } else if (!sensorHistoryMap?.size) {
      setTimeout(() => {
        setLoading(false);
      }, 3000);
    }
  }, [sensorHistoryMap, statusHistoryList, updateStatus, checkedShowMachineStatus, series]);

  // need to make this to be unable to zoom out of range %0 to %100
  const validateZoomRange = (start, end) => ({
    start: Math.max(0, Math.min(start, 100)),
    end: Math.max(0, Math.min(end, 100))
  });

  useEffect(() => {
    const chartInstance = chartRef?.current?.getEchartsInstance();
    if (!chartInstance) return;

    const onZoomRaw = params => {
      const zoom = params.batch ? params.batch[0] : params;
      setDataZoomRange(validateZoomRange(zoom.start, zoom.end));

      if (!template?.sensors?.length) return;

      const allDataPoints = series.flatMap(s => s.data).filter(p => p && p.time);

      if (!allDataPoints.length) return;

      const sorted = allDataPoints.slice().sort((a, b) => new Date(a.time) - new Date(b.time));
      const minTime = new Date(sorted[0].time);
      const maxTime = new Date(sorted[sorted.length - 1].time);

      const startTime = new Date(minTime.getTime() + ((maxTime - minTime) * zoom.start) / 100);
      const endTime = new Date(minTime.getTime() + ((maxTime - minTime) * zoom.end) / 100);

      const averageSensorParamsList = template.sensors.map(s => {
        const millSensor = getMillSensor(s?.type);
        const unit = millSensor?.unit || s?.custom_unit || s?.unit || 'n/a';
        return {
          sensorId: s?.id,
          machineId: s?.machine_id,
          from: startTime.toISOString(),
          to: endTime.toISOString(),
          unit,
          sensor_type: s?.type
        };
      });

      dispatch(loadSensorAverage(averageSensorParamsList));
    };

    const onResizeRaw = () => {
      if (chartRef.current) chartRef.current.getEchartsInstance().resize();
    };

    const onZoom = debounce(onZoomRaw, 400);
    const onResize = debounce(onResizeRaw, 200);

    chartInstance.on('dataZoom', onZoom);
    window.addEventListener('resize', onResize);

    // proper cleanup need to be an arrow function, but ESLint warns about consistent-return
    // eslint-disable-next-line consistent-return
    return () => {
      chartInstance.off('dataZoom', onZoom);
      window.removeEventListener('resize', onResize);
      onZoom.cancel?.();
      onResize.cancel?.();
    };
  }, [chartRef, validateZoomRange, dispatch, loadSensorAverage]); // <- nada de `series` aqui

  function getLeftPadding() {
    return sideSpace(sensorHistoryMap?.size)[0];
  }

  function getRightPadding() {
    return sideSpace(sensorHistoryMap?.size)[1];
  }

  const options = useMemo(
    () => ({
      textStyle: {
        fontFamily: 'Roboto, sans-serif'
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(43, 47, 66, 0.96)',
        textStyle: {
          color: '#ffffff',
          fontFamily: 'Roboto, sans-serif'
        },
        confine: true, // Ensure tooltip stays within chart area
        position(point, _params, _dom, _rect, size) {
          // Point is the mouse position, size is the size of the tooltip
          const [x, y] = point;
          const { contentSize } = size;
          const posX = x + contentSize[0] > size.viewSize[0] - 42 ? x - contentSize[0] - 42 : x + 42;
          const posY = y + contentSize[1] > size.viewSize[1] ? y - contentSize[1] : y;
          return [posX, posY];
        },
        formatter(params) {
          let result = '';
          const currentTooltipTime = params[0].axisValue;
          if (statusHistoryList.length) {
            const status = findClosestStatusDate(currentTooltipTime, statusHistoryList);
            if (status?.v) {
              const hasWarning = status.w;
              const statusDetail = getMachineStatusProps(status.v);
              const statusWarning = t('enum.machine_status.WARNING').toUpperCase();
              const statusDesc = t(`enum.machine_status.${statusDetail.value}`).toUpperCase();
              const ColorSquare = `
                <span
                  style="
                    display:inline-block;
                    margin-right:5px;
                    width:10px;
                    height:10px;
                    background-color:${statusDetail.color};
                  "
                ></span>`;
              const Chip = hasWarning
                ? `
                  <div
                    style="
                      background-color: #ffcc00;
                      color: white;
                      padding: 3px 6px;
                      border-radius: 16px;
                      display: inline-block;
                      font-size: 12px;
                      line-height: 12px;
                    "
                  >
                    ${statusWarning}
                  </div>
                `
                : '';
              result += `${ColorSquare}${statusDesc}${Chip}<br/><br/>`;
            }
          }

          // Extract all sensorIds from params
          const sensorIdsInParams = new Set(params.map(p => p.data.sensorId));

          // Filter the map to exclude entries with sensorIds in params
          const filteredSensorHistory = [...sensorHistoryMap].filter(([sensorId]) => !sensorIdsInParams.has(sensorId));

          // Find the closest time for each filtered entry
          const nearestSensors = filteredSensorHistory.map(([sensorId, historyList]) => {
            // Check if the historyList is empty before trying to reduce
            if (!historyList || historyList.length === 0) {
              return {
                sensorId,
                closestEntry: null // or handle this case as needed
              };
            }
            // Find the entry with the closest time to targetTime
            const closestEntry = historyList.reduce((prev, curr) =>
              Math.abs(curr.t - currentTooltipTime) < Math.abs(prev.t - currentTooltipTime) ? curr : prev
            );
            return {
              sensorId,
              closestEntry
            };
          });

          // Iterate over params and find the sensor details
          const paramAttributes = params.reduce((acc, param) => {
            if (!param.seriesName.includes('STATUS-')) {
              acc.push({
                id: param.data.sensorId,
                name: param.seriesName,
                isStatic: param.data.isStatic,
                unit: param.data.unit,
                color: param.color,
                value: param.value[1]
              });
            }
            return acc;
          }, []);

          // Iterate over the template sensors list to get sensor details
          const nearestSensorsAttribute = findSensorAttributes(nearestSensors);

          // Combine sensors in params and nearest sensors
          const combinedList = paramAttributes
            .concat(nearestSensorsAttribute)
            .filter((o, i, arr) => arr.findIndex(x => x.id === o.id) === i);

          // Sort sensorAttributes alphabetically by 'name'
          combinedList.sort((a, b) => a.name.localeCompare(b.name));

          const solidLine = '<div style="border-top: 2px solid #FFFFFF;margin: 6px 0;"></div>';
          result += combinedList
            .map(
              (sensor, i) => `
                ${i > 0 ? solidLine : ''}
                ${renderCircle(sensor)}${sensor.name}<br/>
                <span style="font-size:16px;font-weight:bold;margin: 6px 0;">${getValueToNullableCell(sensor.value)} ${
                sensor.unit
              }</span>`
            )
            .join('<br/>');
          if (combinedList.length > 0) result += solidLine;
          result += `<p style="margin: 3px 0;"> ${getFormattedDate(currentTooltipTime)}</p>`;
          return result;
        }
      },
      grid: {
        top: '80px',
        left: getLeftPadding(),
        right: getRightPadding(),
        height: `${chartPixelHeight}px`
      },
      xAxis: xAxisDefaultValue,
      yAxis: yAxis.length > 0 ? yAxis : yAxisDefaultValue,
      dataZoom: [
        {
          type: 'slider',
          filterMode: 'none', // Prevents the chart from clipping the line when zooming
          start: dataZoomRange.start,
          end: dataZoomRange.end,
          labelFormatter(value) {
            return `${getFormattedDate(value)}   `;
          }
        }
      ],
      series: series.length > 0 ? series : seriesDefaultValue
    }),
    [yAxis, series, statusHistoryList, sensorHistoryMap]
  );

  const loadTemplates = useCallback(() => {
    dispatch(
      loadGraphsTemplates({
        machineId,
        accessType: ACCESS_TYPE_ALL_ORG
      })
    );
  }, [machineId]);

  useEffect(() => {
    if (!machineId) return;
    loadTemplates();
  }, [machineId]);

  const onChangeTemplate = evt => {
    const templateParam = evt.target.value;
    setTemplateId(templateParam);
  };

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  };

  const handleTooltipOpen = () => {
    setOpenTooltip(true);
  };

  const toggleStatusVisibility = prevSeries =>
    prevSeries.map(s => {
      if (s.name.includes('STATUS-')) {
        const show = !s.show;
        return {
          ...s,
          show,
          lineStyle: { ...s.lineStyle, opacity: show ? 0.6 : 0 }
        };
      }
      return s;
    });

  const handleCheckboxChange = event => {
    setCheckedShowMachineStatus(event.target.checked);
    setSeries(prevSeries => toggleStatusVisibility(prevSeries));
  };

  const toggleSeriesVisibility = (prevSeries, index) =>
    prevSeries.map((s, i) => {
      if (i === index) {
        const show = !s.show;
        return {
          ...s,
          show,
          itemStyle: { opacity: show ? 1 : 0 },
          lineStyle: { ...s.lineStyle, opacity: show ? 1 : 0 }
        };
      }
      return s;
    });

  const toggleLineVisibility = index => {
    setSensorVisibilityList(sensorVisibilityList.map((s, i) => (i === index ? !s : s)));
    setSeries(prevSeries => toggleSeriesVisibility(prevSeries, index));
  };

  const handleGoToTemplates = () => {
    if (!machineId) return;
    history.push(`/dashboard/${encodeURIComponent(machineId)}/graph-templates`);
  };

  return (
    <Card elevation={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={7.5} lg sx={{ order: { xs: 1, sm: 1, md: 1, lg: 1 }, display: 'flex' }}>
          <Stack direction="row" spacing={2}>
            <FormControl
              data-testid="period-time-template-combobox"
              variant="filled"
              sx={{ minWidth: { xs: '100%', sm: '100%', md: '305px' } }}
            >
              <InputLabel id="template-label">{t('batch_records.chart.select_chart')}</InputLabel>
              <Select
                IconComponent={KeyboardArrowDownIcon}
                labelId="period-type-label"
                id="period-type-select"
                onChange={onChangeTemplate}
                value={templateId}
                disabled={loading}
              >
                {templateList.map(tlItem => (
                  <MenuItem key={tlItem.id} value={tlItem.id}>
                    {tlItem.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" color="primary" onClick={handleGoToTemplates}>
              {t('batch_records.chart.template_list')}
            </Button>
          </Stack>
        </Grid>
        {!isDownMd && (
          <Grid item md="auto" lg="auto" sx={{ order: { md: 4, lg: 2 }, display: 'flex', alignItems: 'center' }}>
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <div>
                <Tooltip
                  arrow
                  PopperProps={{
                    disablePortal: true
                  }}
                  onClose={handleTooltipClose}
                  open={openTooltip}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  title={
                    <Box>
                      {MACHINE_STATUS_SIMPLE_MAP.map(status => (
                        <Box
                          key={status.value}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1
                          }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              backgroundColor: status.color,
                              marginRight: 1
                            }}
                          />
                          <Typography variant="body2">{t(`enum.machine_status.${status.value}`)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  }
                >
                  <Button
                    sx={{
                      textTransform: 'none'
                    }}
                    startIcon={<InfoOutlinedIcon />}
                    onClick={handleTooltipOpen}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#000000'
                      }}
                    >
                      {t('batch_records.chart.legend')}
                    </Typography>
                  </Button>
                </Tooltip>
              </div>
            </ClickAwayListener>
          </Grid>
        )}
        <Grid item xs={12} sm={12} md lg="auto" sx={{ order: { xs: 4, sm: 4, md: 5, lg: 3 }, display: 'flex' }}>
          <FormControlLabel
            data-testid="machine-status-change-checkbox-button"
            control={<Checkbox checked={checkedShowMachineStatus} onChange={handleCheckboxChange} disabled={loading} />}
            label={t('batch_records.chart.show_machine_status')}
          />
        </Grid>
      </Grid>
      <Stack spacing={4}>
        {!!resumedSensorMap?.size && !!sensorAverageMap?.size && (
          <>
            {isDownSm ? (
              <Box sx={{ pt: 4 }}>
                <MobileSensorPortrait />
              </Box>
            ) : (
              <div style={{ position: 'relative' }} id="graph-template">
                {loading && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      zIndex: 1000
                    }}
                  >
                    <CircularProgress disableShrink />
                  </div>
                )}
                <ReactEcharts
                  ref={chartRef}
                  data-testid="chart-template-canvas"
                  option={options}
                  opts={{ renderer: 'canvas' }}
                  notMerge
                  lazyUpdate
                  style={{
                    minHeight: `${chartPixelHeightContainer}px`,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                />
              </div>
            )}
            <ChartTemplateFooterContainer
              footerRef={footerRef}
              resumedSensorMap={resumedSensorMap}
              sensorVisibilityList={sensorVisibilityList}
              sensorAverageMap={sensorAverageMap}
              toggleLineVisibility={toggleLineVisibility}
            />
          </>
        )}
      </Stack>
    </Card>
  );
};

SensorsChart.propTypes = {
  machineId: T.number.isRequired,
  date: T.shape().isRequired,
  chartRef: T.shape(),
  footerRef: T.shape()
};

SensorsChart.defaultProps = {
  chartRef: null,
  footerRef: null
};

export default SensorsChart;
